import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  Button,
  Checkbox,
  Flex,
  Text,
  Box,
} from '@radix-ui/themes';

import Card from '../../../../shared/Card';
import Placeholder from '../../../../shared/Placeholder';
import SharedTable from '../../../../shared/Table/Table.jsx';
import useSort from '../../../../shared/Sort/useSort';
import { broadcastTableRow } from './BroadcastTableRow';

import { columns } from '../../utils/constants';
import { useFetchBroadcastMessages } from '../../hooks/useFetchBroadcastMessages';
import {
  BROADCAST_STATUS_TYPES,
  BROADCAST_STATUS_CONFIG,
  BROADCAST_DOT_COLORS,
} from '../../../campaigns/components/CampaignsTable/CalendarView/helpers.js';

const BROADCAST_STATUS_ITEMS = [
  {
    id: BROADCAST_STATUS_TYPES.DRAFT,
    label: BROADCAST_STATUS_CONFIG.draft.label,
  },
  {
    id: BROADCAST_STATUS_TYPES.SCHEDULED,
    label: BROADCAST_STATUS_CONFIG.scheduled.label,
  },
  {
    id: BROADCAST_STATUS_TYPES.SENT,
    label: BROADCAST_STATUS_CONFIG.sent.label,
  },
];

export const Table = () => {
  const { t } = useTranslation('broadcastMessages');

  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const itemsPerPage = 10;

  const { sortingStatus, changeSort } = useSort({ persistInUrl: false });

  const handleStatusFilterChange = statusId => {
    setSelectedStatusFilter(prev => {
      if (prev.includes(statusId)) {
        return prev.filter(id => id !== statusId);
      }
      return [...prev, statusId];
    });
    setPage(1);
  };

  const onPageChange = data => setPage(data.selected + 1);

  const { isLoading, broadcastMessages, pagy } = useFetchBroadcastMessages({
    page,
    itemsPerPage,
    showAll,
    statusFilters:
      selectedStatusFilter.length > 0 ? selectedStatusFilter : undefined,
    ...sortingStatus,
  });

  const filterDropdown = (
    <Box ml="auto">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button variant="outline" size="2">
            <i className="fa-regular fa-filter" />
            <Text size="2">{t('filter')}</Text>
            <i className="fa-solid fa-chevron-down" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {BROADCAST_STATUS_ITEMS.map(item => (
            <DropdownMenu.Item
              key={item.id}
              onSelect={e => {
                e.preventDefault();
                handleStatusFilterChange(item.id);
              }}
            >
              <Flex align="center" gap="2">
                <Checkbox checked={selectedStatusFilter.includes(item.id)} />
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: BROADCAST_DOT_COLORS[item.id],
                  }}
                />
                <Text size="2">{item.label}</Text>
              </Flex>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </Box>
  );

  return (
    <Card
      title={t('historyTitle')}
      items={!showAll && broadcastMessages.length ? itemsPerPage : undefined}
      pagy={!showAll && broadcastMessages.length ? pagy : undefined}
      onPageChange={
        !showAll && broadcastMessages.length ? onPageChange : undefined
      }
      onClick={() => setShowAll(!showAll)}
      canShowAll={broadcastMessages.length > 0}
      isShowingAll={showAll}
      isLoading={isLoading}
      margin="0"
      headerContent={filterDropdown}
    >
      {broadcastMessages.length ? (
        <SharedTable
          columns={columns}
          body={broadcastTableRow({
            broadcastMessages: broadcastMessages,
          })}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
          minWidthBeforeScroll="100%"
          id="engagement-table"
        />
      ) : (
        <Placeholder />
      )}
    </Card>
  );
};
